/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useReducer,
  useContext,
  FC,
  useCallback,
  useEffect
} from 'react';
import { useAppDispatch } from 'store/hooks';
import { setIsLoggedIn } from 'store/user/slice';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  State,
  Dispatch,
  ActionTypes,
  DispatchContext,
  IAuthProps,
  LoginForm
} from './types';
import reducer from './reducers';
import { Loading } from 'components/ui';
import { refreshTokenStorage, tokenStorage } from 'utils/storage';
import { instance } from 'utils/api';
import { BlockedModal } from 'components/shared';

const initialState: State = {
  from: '',
  phone: '',
  email: '',
  isBlockedVisible: false,
  tmpToken: '',
  redirectPath: '',
  isLoading: !!tokenStorage.get(),
  user: null
};

const AuthStateContext = createContext<State | undefined>(undefined);
const AuthDispatchContext = createContext<
  | {
      dispatch: Dispatch;
      getUserData: DispatchContext['getUserData'];
      signOut: DispatchContext['signOut'];
    }
  | undefined
>(undefined);

const AuthProvider: FC<IAuthProps> = ({ children }) => {
  const dispatchRedux = useAppDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);

  const signOut = useCallback(() => {
    instance.get('/auth/logout', {
      headers: {
        Authorization: `Bearer ${tokenStorage.get()}`
      }
    });
    tokenStorage.remove();
    refreshTokenStorage.remove();
    dispatchRedux(setIsLoggedIn(false));
  }, [dispatchRedux]);

  const getUserData = useCallback(async () => {
    try {
      dispatch({
        type: ActionTypes.SET_LOADING,
        data: true
      });
      const res = await instance.get('/auth/me');

      dispatch({
        type: ActionTypes.SET_USER,
        data: res.data
      });

      dispatchRedux(setIsLoggedIn(true));

      return res.data;
    } catch {
      signOut();
    } finally {
      dispatch({
        type: ActionTypes.SET_LOADING,
        data: false
      });
    }
  }, [dispatch, dispatchRedux, signOut]);

  useEffect(() => {
    if (tokenStorage.get()) {
      getUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    dispatch({
      type: ActionTypes.SET_BLOCKED_VISIBLE,
      data: false
    });
  };

  if (state.isLoading) {
    return <Loading />;
  }

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={{ dispatch, signOut, getUserData }}>
        {children}
        <BlockedModal open={state.isBlockedVisible} onClose={closeModal} />
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

const useAuthStateContext = (): State => {
  const context = useContext(AuthStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useAuthStateContext must be used within a useAuthStateContext'
    );
  }

  return context;
};

const useAuthDispatchContext = (): DispatchContext => {
  const context = useContext(AuthDispatchContext);
  const { executeRecaptcha } = useGoogleReCaptcha();

  if (typeof context === 'undefined') {
    throw new Error(
      'useAuthDispatchContext must be used within a useAuthDispatchContext'
    );
  }

  const { dispatch, getUserData, signOut } = context;

  const refetchUserData = useCallback(async () => {
    try {
      const res = await instance.get('/auth/me');

      dispatch({
        type: ActionTypes.SET_USER,
        data: res.data
      });

      return res.data;
    } catch {
      //
    }
  }, [dispatch]);

  const signIn = useCallback(
    async (params: LoginForm) => {
      try {
        if (!executeRecaptcha) {
          dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });

          return Promise.reject('');
        }

        const recaptchaToken = await executeRecaptcha('login');

        const { data } = await instance.post(
          '/auth/sign-in',
          {
            ...params,
            recaptchaToken,
            role: 'doctor'
          },
          {
            withCredentials: true
          }
        );

        dispatch({
          type: ActionTypes.SET_TMP_TOKEN,
          data: data.tmpToken
        });

        return data;
      } catch (err: any) {
        if (err?.response?.status === 429) {
          dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });

          return Promise.reject(err);
        }

        return Promise.reject(err);
      }
    },
    [dispatch, executeRecaptcha]
  );

  const verify = useCallback(
    async (key: string) => {
      try {
        const { data } = await instance.get('/auth/verify', {
          params: {
            key
          }
        });

        return data.data;
      } catch (err: any) {
        if (err.response?.status === 429) {
          return dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });
        }

        return Promise.reject(err);
      }
    },
    [dispatch]
  );

  const forgot = useCallback(
    async (email: string) => {
      try {
        if (!executeRecaptcha) {
          dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });

          return Promise.reject('');
        }

        const recaptchaToken = await executeRecaptcha('password_reset');

        const { data } = await instance.post('/auth/forgot-password', {
          email,
          recaptchaToken,
          role: 'doctor'
        });

        return data;
      } catch (err: any) {
        if (err?.status === 429) {
          return dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });
        }

        return Promise.reject(err);
      }
    },
    [dispatch, executeRecaptcha]
  );

  const setPassword = useCallback(
    async (password: string, key: string) => {
      try {
        const { data } = await instance.post('/auth/reset-password', {
          token: key,
          newPassword: password
        });

        return data;
      } catch (err: any) {
        if (err.response?.status === 429) {
          return dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });
        }

        return Promise.reject(err);
      }
    },
    [dispatch]
  );

  const setRedirectUrl = useCallback(
    data => {
      dispatch({
        type: ActionTypes.SET_REDIRECT_PATH,
        data
      });
    },
    [dispatch]
  );

  const verifyEmail = useCallback(
    async (email: string, code: string) => {
      try {
        const { data } = await instance.post('/auth/verify/email', {
          email,
          code
        });

        if (data?.redirectUrl) {
          setRedirectUrl(data.redirectUrl);
        }

        return data;
      } catch (err: any) {
        if (err.response?.status === 429) {
          return dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });
        }

        return Promise.reject(err);
      }
    },
    [dispatch, setRedirectUrl]
  );

  const verifyPhone = useCallback(
    async (phone: string, code: string) => {
      try {
        const { data } = await instance.post('/auth/verify/phone', {
          phone,
          code
        });

        return data;
      } catch (err: any) {
        if (err.response?.status === 429) {
          return dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });
        }

        return Promise.reject(err);
      }
    },
    [dispatch]
  );

  const verify2FA = useCallback(async (otp: string, tmpToken: string) => {
    try {
      const data = await instance.post('/auth/otp/verify', {
        otp,
        tmpToken
      });

      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  }, []);

  return {
    signOut,
    signIn,
    verify,
    forgot,
    setPassword,
    verifyEmail,
    verifyPhone,
    setRedirectUrl,
    verify2FA,
    getUserData,
    refetchUserData
  };
};

export default AuthProvider;
export { useAuthDispatchContext, useAuthStateContext };
